/**
 * ============================================================
 * ALTENS — ACTUALITÉS
 * ============================================================
 *
 * Gestion :
 * - exclusion des articles mis en avant côté PHP
 * - recherche AJAX
 * - filtres AJAX par catégorie
 * - combinaison recherche + catégorie
 * - état actif des filtres
 * - message aucun résultat
 * - chargement AJAX "Voir plus"
 */

const initNews = () => {
    const newsSections = document.querySelectorAll(".news-page");

    if (!newsSections.length) {
        return;
    }

    newsSections.forEach((section) => {
        const searchInput = section.querySelector("[data-news-search]");

        const categoryButtons = Array.from(section.querySelectorAll("[data-news-category]"));

        const grid = section.querySelector("[data-news-grid]");

        const noResults = section.querySelector("[data-news-no-results]");

        const loadMoreButton = section.querySelector("[data-news-load-more]");

        if (!grid) {
            return;
        }

        /**
         * ====================================================
         * ÉTAT
         * ====================================================
         */

        let activeCategory = "";

        let searchTerm = "";

        let searchTimer = null;

        let currentRequest = null;

        const perPage = Number(loadMoreButton?.dataset.perPage || 8);

        /**
         * Wrapper du bouton.
         *
         * Il ne doit jamais être supprimé du DOM :
         * une autre catégorie peut avoir davantage
         * de résultats et avoir besoin du bouton.
         */

        const loadMoreWrapper = loadMoreButton?.parentElement || null;

        const loadMoreLabel = loadMoreButton?.querySelector("[data-load-more-label]") || null;

        const defaultLoadMoreLabel = loadMoreLabel?.textContent?.trim() || "VOIR PLUS";

        /**
         * ====================================================
         * OFFSET INITIAL
         * ====================================================
         *
         * home.php affiche :
         *
         * - 3 articles mis en avant
         * - puis 8 articles dans la grille
         *
         * Le PHP AJAX exclut maintenant lui-même les
         * 3 articles mis en avant.
         *
         * L'offset relatif de la grille doit donc être 8,
         * et non 11.
         */

        if (loadMoreButton) {
            const initialIndex = Number(loadMoreButton.dataset.index || 0);

            loadMoreButton.dataset.offset = String(initialIndex);
        }

        /**
         * ====================================================
         * ÉTAT DES BOUTONS CATÉGORIES
         * ====================================================
         */

        const updateCategoryButtons = () => {
            categoryButtons.forEach((button) => {
                const category = button.dataset.newsCategory || "";

                const isActive = category === activeCategory;

                button.classList.toggle("is-active", isActive);

                button.classList.toggle("bg-primary-green", isActive);

                button.classList.toggle("text-white", isActive);

                button.classList.toggle("bg-green-light", !isActive);

                button.classList.toggle("text-primary-green", !isActive);

                button.setAttribute("aria-pressed", isActive ? "true" : "false");
            });
        };

        /**
         * ====================================================
         * MESSAGE AUCUN RÉSULTAT
         * ====================================================
         */

        const updateNoResults = (total) => {
            if (!noResults) {
                return;
            }

            noResults.classList.toggle("hidden", total > 0);
        };

        /**
         * ====================================================
         * VOIR PLUS
         * ====================================================
         */

        const showLoadMore = () => {
            if (!loadMoreWrapper) {
                return;
            }

            loadMoreWrapper.classList.remove("hidden");
        };

        const hideLoadMore = () => {
            if (!loadMoreWrapper) {
                return;
            }

            loadMoreWrapper.classList.add("hidden");
        };

        const setLoadMoreLoading = (isLoading) => {
            if (!loadMoreButton) {
                return;
            }

            loadMoreButton.disabled = isLoading;

            loadMoreButton.classList.toggle("is-loading", isLoading);

            if (!loadMoreLabel) {
                return;
            }

            loadMoreLabel.textContent = isLoading ? window.altensNews?.loadingText || "CHARGEMENT..." : defaultLoadMoreLabel;
        };

        /**
         * ====================================================
         * AJAX
         * ====================================================
         *
         * reset = true
         *
         * Nouvelle catégorie ou nouvelle recherche.
         * La grille est entièrement remplacée.
         *
         *
         * reset = false
         *
         * Clic sur "Voir plus".
         * Les nouveaux articles sont ajoutés.
         */

        const fetchNews = async ({ reset = false } = {}) => {
            /**
             * Annule une éventuelle requête
             * précédente.
             */

            if (currentRequest) {
                currentRequest.abort();
            }

            const requestController = new AbortController();

            currentRequest = requestController;

            /**
             * Nouvelle catégorie / recherche :
             * on repart du début de la grille.
             */

            const offset = reset ? 0 : Number(loadMoreButton?.dataset.offset || 0);

            /**
             * L'index sert uniquement au pattern
             * graphique des cartes.
             */

            const index = reset ? 0 : Number(loadMoreButton?.dataset.index || 0);

            /**
             * ------------------------------------------------
             * FORM DATA
             * ------------------------------------------------
             */

            const formData = new FormData();

            formData.append("action", "altens_load_more_news");

            formData.append("nonce", window.altensNews?.nonce || "");

            formData.append("offset", String(offset));

            formData.append("index", String(index));

            formData.append("per_page", String(perPage));

            formData.append("category", activeCategory);

            formData.append("search", searchTerm);

            /**
             * ------------------------------------------------
             * LOADING
             * ------------------------------------------------
             */

            setLoadMoreLoading(true);

            if (reset) {
                grid.classList.add("is-loading");

                hideLoadMore();

                if (noResults) {
                    noResults.classList.add("hidden");
                }
            }

            try {
                const response = await fetch(window.altensNews.ajaxUrl, {
                    method: "POST",

                    body: formData,

                    credentials: "same-origin",

                    signal: requestController.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const result = await response.json();

                if (!result.success || !result.data) {
                    throw new Error("Invalid AJAX response");
                }

                const data = result.data;

                const html = data.html || "";

                const total = Number(data.total || 0);

                const nextOffset = Number(data.next_offset || 0);

                const nextIndex = Number(data.next_index || 0);

                const hasMore = Boolean(data.has_more);

                /**
                 * ------------------------------------------------
                 * INJECTION HTML
                 * ------------------------------------------------
                 */

                if (reset) {
                    /**
                     * Nouvelle recherche :
                     * remplace la grille.
                     */

                    grid.innerHTML = html;
                } else {
                    /**
                     * Voir plus :
                     * ajoute les cartes.
                     */

                    const wrapper = document.createElement("div");

                    wrapper.innerHTML = html;

                    Array.from(wrapper.children).forEach((card) => {
                        grid.appendChild(card);
                    });
                }

                /**
                 * ------------------------------------------------
                 * ÉTAT AJAX
                 * ------------------------------------------------
                 */

                if (loadMoreButton) {
                    loadMoreButton.dataset.offset = String(nextOffset);

                    loadMoreButton.dataset.index = String(nextIndex);

                    loadMoreButton.dataset.total = String(total);
                }

                /**
                 * ------------------------------------------------
                 * AUCUN RÉSULTAT
                 * ------------------------------------------------
                 */

                updateNoResults(total);

                /**
                 * ------------------------------------------------
                 * VOIR PLUS
                 * ------------------------------------------------
                 */

                if (loadMoreButton && hasMore && total > 0) {
                    showLoadMore();
                } else {
                    hideLoadMore();
                }
            } catch (error) {
                /**
                 * AbortError est normal :
                 *
                 * l'utilisateur a changé rapidement
                 * de catégorie ou de recherche.
                 */

                if (error.name === "AbortError") {
                    return;
                }

                console.error("News AJAX:", error);

                if (loadMoreLabel) {
                    loadMoreLabel.textContent = window.altensNews?.errorText || "UNE ERREUR EST SURVENUE";
                }

                window.setTimeout(() => {
                    if (loadMoreLabel) {
                        loadMoreLabel.textContent = defaultLoadMoreLabel;
                    }
                }, 2500);
            } finally {
                /**
                 * Une ancienne requête annulée ne doit
                 * pas modifier l'état d'une nouvelle
                 * requête encore active.
                 */

                if (currentRequest !== requestController) {
                    return;
                }

                grid.classList.remove("is-loading");

                setLoadMoreLoading(false);

                currentRequest = null;
            }
        };

        /**
         * ====================================================
         * CATÉGORIES
         * ====================================================
         */

        categoryButtons.forEach((button) => {
            button.addEventListener("click", () => {
                const category = button.dataset.newsCategory || "";

                /**
                 * Ne recharge pas la catégorie
                 * déjà sélectionnée.
                 */

                if (category === activeCategory) {
                    return;
                }

                activeCategory = category;

                updateCategoryButtons();

                /**
                 * Nouvelle catégorie :
                 *
                 * offset = 0
                 * index = 0
                 */

                fetchNews({
                    reset: true,
                });
            });
        });

        /**
         * ====================================================
         * RECHERCHE
         * ====================================================
         */

        if (searchInput) {
            searchInput.addEventListener("input", () => {
                window.clearTimeout(searchTimer);

                /**
                 * Petit délai afin d'éviter
                 * une requête à chaque frappe.
                 */

                searchTimer = window.setTimeout(() => {
                    searchTerm = searchInput.value.trim();

                    fetchNews({
                        reset: true,
                    });
                }, 350);
            });
        }

        /**
         * ====================================================
         * VOIR PLUS
         * ====================================================
         */

        if (loadMoreButton) {
            loadMoreButton.addEventListener("click", () => {
                if (loadMoreButton.disabled) {
                    return;
                }

                fetchNews({
                    reset: false,
                });
            });
        }

        /**
         * ====================================================
         * INITIALISATION
         * ====================================================
         */

        updateCategoryButtons();

        /**
         * On conserve la grille générée par home.php
         * au premier affichage.
         *
         * Aucune requête AJAX supplémentaire.
         */

        updateNoResults(grid.querySelectorAll("[data-news-card]").length);
    });
};

export default initNews;
